import { create } from "zustand";
import { persist } from "zustand/middleware";

interface AuthUser {
  email: string;
  role: string;
  first_name: string | null;
  last_name: string | null;
  organization_id: string | null;
  organization_name: string | null;
  plan: string | null;
  primary_color: string | null;
  logo_url: string | null;
}

interface ImpersonationBackup {
  accessToken: string;
  refreshToken: string;
  user: AuthUser;
}

interface AuthState {
  accessToken: string | null;
  refreshToken: string | null;
  user: AuthUser | null;
  impersonating: ImpersonationBackup | null;
  setTokens: (
    access: string,
    refresh: string,
    role: string,
    email: string,
    first_name?: string | null,
    last_name?: string | null,
    organization_id?: string | null,
    organization_name?: string | null,
    plan?: string | null,
    primary_color?: string | null,
    logo_url?: string | null,
  ) => void;
  updateBranding: (primary_color: string | null, logo_url: string | null) => void;
  updateProfile: (first_name: string | null, last_name: string | null) => void;
  startImpersonation: (access: string, refresh: string, targetUser: AuthUser) => void;
  stopImpersonation: () => void;
  logout: () => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      accessToken: null,
      refreshToken: null,
      user: null,
      impersonating: null,
      setTokens: (
        access, refresh, role, email,
        first_name = null, last_name = null,
        organization_id = null, organization_name = null, plan = null,
        primary_color = null, logo_url = null,
      ) =>
        set({
          accessToken: access,
          refreshToken: refresh,
          user: { email, role, first_name, last_name, organization_id, organization_name, plan, primary_color, logo_url },
          impersonating: null,
        }),
      updateBranding: (primary_color, logo_url) =>
        set(state => ({
          user: state.user ? { ...state.user, primary_color, logo_url } : null,
        })),
      updateProfile: (first_name, last_name) =>
        set(state => ({
          user: state.user ? { ...state.user, first_name, last_name } : null,
        })),
      startImpersonation: (access, refresh, targetUser) => {
        const current = get();
        set({
          impersonating: {
            accessToken: current.accessToken!,
            refreshToken: current.refreshToken!,
            user: current.user!,
          },
          accessToken: access,
          refreshToken: refresh,
          user: targetUser,
        });
      },
      stopImpersonation: () => {
        const backup = get().impersonating;
        if (!backup) return;
        set({
          accessToken: backup.accessToken,
          refreshToken: backup.refreshToken,
          user: backup.user,
          impersonating: null,
        });
      },
      logout: () => set({ accessToken: null, refreshToken: null, user: null, impersonating: null }),
    }),
    { name: "auth" }
  )
);
